import { useSettings } from "../../context/SettingsContext";
import {
    Toggle,
    SettingRow,
    SectionCard,
    ButtonGroup,
    NumberInput,
    ColorInput,
    SelectInput,
} from "../ui/index";

const ZOOM_TYPES = [
    { value: "lens", label: "Lens" },
    { value: "window", label: "Window" },
    { value: "inner", label: "Inner" },
];

const LENS_SHAPES = [
    { value: "circle", label: "Circle" },
    { value: "square", label: "Square" },
];

const CURSORS = [
    { value: "crosshair", label: "Crosshair" },
    { value: "zoom-in", label: "Zoom-in" },
    { value: "default", label: "Default" },
];

export default function ZoomPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    return (
        <div className="space-y-5">
            <SectionCard
                title="Activation"
                description="Control when zoom is triggered."
            >
                <SettingRow
                    label="Zoom on Hover"
                    description="Activate zoom when the cursor hovers over the main image."
                >
                    <Toggle
                        value={settings.zoomOnHover}
                        onChange={(v) => s("zoomOnHover", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Cursor Style"
                    description="Mouse cursor appearance over the main image."
                >
                    <SelectInput
                        options={CURSORS}
                        value={settings.zoomCursor}
                        onChange={(v) => s("zoomCursor", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Zoom Type"
                description="Choose how the magnified image is displayed."
            >
                <SettingRow
                    label="Display Mode"
                    description="Lens overlays the image, Window shows a separate panel, Inner zooms inside the image."
                >
                    <ButtonGroup
                        options={ZOOM_TYPES}
                        value={settings.zoomType}
                        onChange={(v) => s("zoomType", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Zoom Level"
                    description="Magnification multiplier applied to the full-size image."
                >
                    <NumberInput
                        value={settings.zoomLevel}
                        onChange={(v) => s("zoomLevel", v)}
                        min={1}
                        max={10}
                        step={0.5}
                    />
                </SettingRow>
            </SectionCard>

            {settings.zoomType !== "inner" && (
                <SectionCard
                    title="Easing"
                    description="Smooth the zoom movement for a polished feel."
                >
                    <SettingRow
                        label="Enable Easing"
                        description="Apply a smooth follow-through to zoom position updates."
                    >
                        <Toggle
                            value={settings.zoomEasing}
                            onChange={(v) => s("zoomEasing", v)}
                        />
                    </SettingRow>

                    {settings.zoomEasing && (
                        <SettingRow
                            label="Easing Amount"
                            description="Easing lag factor. Higher values produce a slower, more fluid follow."
                        >
                            <NumberInput
                                value={settings.zoomEasingAmount}
                                onChange={(v) => s("zoomEasingAmount", v)}
                                min={1}
                                max={50}
                            />
                        </SettingRow>
                    )}
                </SectionCard>
            )}

            {settings.zoomType === "lens" && (
                <SectionCard
                    title="Lens Appearance"
                    description="Customize the zoom lens overlay."
                >
                    <SettingRow
                        label="Lens Size"
                        description="Diameter or width of the circular/square lens in pixels."
                    >
                        <NumberInput
                            value={settings.lensSize}
                            onChange={(v) => s("lensSize", v)}
                            min={50}
                            max={500}
                            unit="px"
                        />
                    </SettingRow>

                    <SettingRow
                        label="Lens Shape"
                        description="Visual shape of the zoom lens."
                    >
                        <ButtonGroup
                            options={LENS_SHAPES}
                            value={settings.lensShape}
                            onChange={(v) => s("lensShape", v)}
                        />
                    </SettingRow>

                    <SettingRow
                        label="Border Color"
                        description="Color of the lens border ring."
                    >
                        <ColorInput
                            value={settings.lensBorderColor}
                            onChange={(v) => s("lensBorderColor", v)}
                        />
                    </SettingRow>

                    <SettingRow
                        label="Border Width"
                        description="Thickness of the lens border in pixels."
                    >
                        <NumberInput
                            value={settings.lensBorderWidth}
                            onChange={(v) => s("lensBorderWidth", v)}
                            min={0}
                            max={20}
                            unit="px"
                        />
                    </SettingRow>
                </SectionCard>
            )}
        </div>
    );
}
